import React, { useEffect, useRef, createElement } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import { trackInitiateCheckout, trackLead } from '../../utils/metaPixel';
const GHL_FORM_ID = 'ydIZJTgbcyUzjldi7fjM';
const GHL_FORM_URL = `https://api.leadconnectorhq.com/widget/form/${GHL_FORM_ID}`;
const GHL_EMBED_SCRIPT = 'https://link.msgsndr.com/js/form_embed.js';
/**
 * PIXEL ARCHITECTURE — Lipo Laser · Beverly Hills
 *
 * The landing IS the funnel and owns every Meta event.
 * GHL only renders the form; its native pixel field is intentionally left
 * empty (per GHL's own guidance: "Ignore this field if you plan to use this
 * form/survey inside a funnel").
 *
 * Landing-side events (Pixel 6810483222354785):
 *   - PageView         → App.tsx, on mount
 *   - InitiateCheckout → here, when the form section enters viewport
 *   - Lead             → here, on GHL form submission success postMessage
 *
 * The Lead listener is STRICT to prevent false positives:
 *   - Only fires on whitelisted exact submission-success signatures
 *   - Only fires ONCE per page load (one-shot guard)
 *   - Origin-checked to leadconnectorhq.com / msgsndr.com only
 *   - No substring matching (loose matching previously caused ~17x inflation
 *     vs real CRM submissions because lifecycle messages matched "submit")
 */
export function Contact() {
  const {
    t
  } = useLanguage();
  const easeOutCubic = [0.33, 1, 0.68, 1];
  const sectionRef = useRef<HTMLElement | null>(null);
  const initiateFiredRef = useRef(false);
  const leadFiredRef = useRef(false);
  useEffect(() => {
    // 1) Inject GHL embed script once
    if (!document.querySelector(`script[src="${GHL_EMBED_SCRIPT}"]`)) {
      const script = document.createElement('script');
      script.src = GHL_EMBED_SCRIPT;
      script.async = true;
      document.body.appendChild(script);
    }
    // 2) Lead listener — strict whitelist of GHL success signatures
    const SUCCESS_SIGNATURES = ['form_submit_success', 'form:submit:success', 'form_submitted', 'formsubmitted', 'form-submit-success'];
    const isSuccessSignature = (value: unknown): boolean => {
      if (typeof value !== 'string') return false;
      return SUCCESS_SIGNATURES.includes(value.toLowerCase().trim());
    };
    const handleMessage = (event: MessageEvent) => {
      if (leadFiredRef.current) return;
      const origin = event.origin || '';
      if (!origin.includes('leadconnectorhq.com') && !origin.includes('msgsndr.com')) return;
      const data = event.data;
      // Debug log — exact payloads visible in DevTools console
      if (typeof console !== 'undefined') {
        console.debug('[GHL postMessage]', data);
      }
      let matched = false;
      if (typeof data === 'string') {
        matched = isSuccessSignature(data);
      } else if (data && typeof data === 'object') {
        const obj = data as Record<string, unknown>;
        matched = isSuccessSignature(obj.type) || isSuccessSignature(obj.event) || isSuccessSignature(obj.eventName) || isSuccessSignature(obj.action) || obj.submitted === true;
      }
      if (matched) {
        leadFiredRef.current = true;
        trackLead({
          form_id: GHL_FORM_ID,
          source: 'landing_postmessage'
        });
      }
    };
    window.addEventListener('message', handleMessage);
    // 3) InitiateCheckout — fire when the section enters viewport (intent signal)
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !initiateFiredRef.current) {
          initiateFiredRef.current = true;
          trackInitiateCheckout({
            form_id: GHL_FORM_ID
          });
        }
      });
    }, {
      threshold: 0.25
    });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      window.removeEventListener('message', handleMessage);
      observer.disconnect();
    };
  }, []);
  const benefits: string[] = t('contact.benefits');
  return <section id="contacto" ref={sectionRef} className="py-20 lg:py-28 bg-brand-dark text-brand-cream relative overflow-hidden" data-id="element-70">
      {/* Subtle grain */}
      <div className="absolute inset-0 bg-grain opacity-50 pointer-events-none" data-id="element-71"></div>

      <div className="container mx-auto px-6 relative z-10" data-id="element-72">
        <div className="grid lg:grid-cols-12 gap-16 lg:gap-24 items-start max-w-6xl mx-auto" data-id="element-73">
          {/* Left column: persuasion */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 1,
          ease: easeOutCubic
        }} className="lg:col-span-5 pt-8" data-id="element-74">
            <div className="inline-flex items-center gap-3 mb-8" data-id="element-75">
              <span className="w-8 h-px bg-brand-gold" data-id="element-76"></span>
              <span className="text-brand-gold/80 font-medium tracking-[0.25em] text-[10px] uppercase" data-id="element-77">
                {t('contact.eyebrow')}
              </span>
            </div>

            <h2 className="text-5xl lg:text-6xl font-serif font-light mb-8 leading-[1.05] tracking-tight" data-id="element-78">
              {t('contact.heading')}
            </h2>

            <p className="text-brand-cream/70 text-lg font-light leading-relaxed mb-12" data-id="element-79">
              {t('contact.subhead')}
            </p>

            {/* Availability signal */}
            <div className="mb-12 pl-6 border-l border-brand-gold/40" data-id="element-80">
              <p className="font-serif italic text-brand-cream/60 text-sm leading-relaxed" data-id="element-81">
                {t('contact.urgency')}
              </p>
            </div>

            {/* Benefits list */}
            <ul className="space-y-5 mb-12" data-id="element-82">
              {benefits.map((b, i) => <li key={i} className="flex items-start gap-4" data-id="element-83">
                  <span className="text-brand-gold/70 font-serif italic text-sm shrink-0 mt-1" data-id="element-84">
                    0{i + 1}
                  </span>
                  <span className="text-brand-cream/85 font-light leading-relaxed" data-id="element-85">
                    {b}
                  </span>
                </li>)}
            </ul>

            {/* Financing note */}
            <div className="p-5 border border-brand-cream/15 rounded-2xl mb-12" data-id="element-86">
              <p className="text-[10px] font-medium tracking-widest uppercase text-brand-gold/80 mb-2" data-id="element-87">
                Financing
              </p>
              <p className="text-sm text-brand-cream/70 font-light leading-relaxed" data-id="element-88">
                {t('contact.financingNote')}
              </p>
            </div>

            {/* Trust signals */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-brand-cream/10" data-id="element-89">
              <div data-id="element-90">
                <p className="text-[10px] font-medium tracking-widest uppercase text-brand-cream/40 mb-1" data-id="element-91">
                  {t('contact.trust.secure')}
                </p>
              </div>
              <div data-id="element-92">
                <p className="text-[10px] font-medium tracking-widest uppercase text-brand-cream/40 mb-1" data-id="element-93">
                  {t('contact.trust.response')}
                </p>
              </div>
              <div data-id="element-94">
                <p className="text-[10px] font-medium tracking-widest uppercase text-brand-cream/40 mb-1" data-id="element-95">
                  {t('contact.trust.bilingual')}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Right column: stable inline GHL form */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 1,
          delay: 0.2,
          ease: easeOutCubic
        }} className="lg:col-span-7 lg:sticky lg:top-24 self-start rounded-[2rem] overflow-hidden border border-brand-gold/20 bg-brand-cream shadow-2xl shadow-black/15" data-id="element-96">
            <iframe src={GHL_FORM_URL} style={{
            display: 'block',
            width: '100%',
            height: '900px',
            border: 'none',
            borderRadius: '0'
          }} id={`inline-${GHL_FORM_ID}`} data-layout={`{'id':'INLINE'}`} data-trigger-type="alwaysShow" data-trigger-value="" data-activation-type="alwaysActivated" data-activation-value="" data-deactivation-type="neverDeactivate" data-deactivation-value="" data-form-name="Form Los Angeles" data-height="900" data-layout-iframe-id={`inline-${GHL_FORM_ID}`} data-form-id={GHL_FORM_ID} title="Form Los Angeles" data-id="element-97" />
          </motion.div>
        </div>
      </div>
    </section>;
}